/**
 * `GET /casos/{id}/informacoes` (`TP-072`). O IW é lido na hora, em nome de quem abre a aba: fora do ar, a resposta traz
 * o resto e diz `indisponivel`.
 */

import { queryOptions } from '@tanstack/react-query';
import type { Schemas } from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';
import type { CampoDoCadastro } from '../documentos/tipos';
import type {
  FonteDaInformacao,
  FormularioDoIw,
  GrupoDaInformacao,
  InformacaoDoCaso,
  InformacoesDoCaso,
  SituacaoDoIw,
  TipoDaFonte,
} from './tipos';

type Fio = Schemas['InformacoesDoCasoResponse'];

export const chavesDeInformacoes = {
  doCaso: (casoId: string) => ['casos', casoId, 'informacoes'] as const,
};

function data(texto: string | null | undefined): Date | null {
  return texto ? new Date(texto) : null;
}

function mapearFonte(f: Schemas['FonteDaInformacaoResponse']): FonteDaInformacao {
  return {
    tipo: (f.tipo ?? 'documento') as TipoDaFonte,
    rotulo: f.rotulo ?? '',
    em: data(f.em),
    documentoId: f.documentoId ?? null,
    item: f.item ?? null,
    ticketId: f.ticketId ?? null,
    emailId: f.emailId ?? null,
    anexoId: f.anexoId ?? null,
    tipoDeCuidado: f.tipoDeCuidado ?? null,
    aConfirmar: f.aConfirmar,
  };
}

function mapearInformacao(f: Schemas['InformacaoDoCasoResponse']): InformacaoDoCaso {
  return {
    chave: f.chave ?? '',
    rotulo: f.rotulo ?? f.chave ?? '',
    grupo: (f.grupo ?? 'documento') as GrupoDaInformacao,
    cadastro: (f.cadastro ?? null) as CampoDoCadastro | null,
    diverge: f.diverge,
    valores: (f.valores ?? []).map((v) => ({ valor: v.valor ?? '', fontes: (v.fontes ?? []).map(mapearFonte) })),
  };
}

export function mapearInformacoesDoCaso(fio: Fio): InformacoesDoCaso {
  return {
    dados: (fio.dados ?? []).map(mapearInformacao),
    iw: {
      situacao: (fio.iw.situacao ?? 'indisponivel') as SituacaoDoIw,
      admissaoId: fio.iw.admissaoId ?? null,
      admissaoAnteriorAoCaso: fio.iw.admissaoAnteriorAoCaso,
      provaNova: fio.iw.provaNova,
      formularios: (fio.iw.formularios ?? []).map((f) => ({
        tipo: (f.tipo ?? 'nead') as FormularioDoIw['tipo'],
        template: f.template ?? '',
        data: data(f.data),
        assinada: f.assinada,
      })),
    },
  };
}

export function informacoesDoCaso(casoId: string) {
  return queryOptions({
    queryKey: chavesDeInformacoes.doCaso(casoId),
    queryFn: ({ signal }) =>
      requisitar<Fio>(`/casos/${encodeURIComponent(casoId)}/informacoes`, { sinal: signal }).then(mapearInformacoesDoCaso),
    // Cada leitura vai ao IW em nome de quem abre: o foco na janela não relê. O que a pessoa faz no Caso relê.
    refetchOnWindowFocus: false,
  });
}
